// Dependencies
import { createSlice } from '@reduxjs/toolkit';
import { differenceInCalendarDays, isSameDay, subYears } from 'date-fns';
import { PayloadAction } from '@reduxjs/toolkit/dist/createAction';
// Actions
import { setEvent } from '../../event/redux/reducer';
// Types
import { PreCartData } from '../objects/preCartData';
import { Traveler } from '../../trip/objects/traveler';
import { Accommodation } from '../objects/accommodation';
import { AccommodationRoom } from '../objects/accommodationRoom';
import { AccommodationState } from '../objects/accommodationState';
import { GiataAccommodation } from '../objects/giataAccommodation';
import { AccommodationProduct } from '../objects/accommodationProduct';
import { AccommodationRoomPrices } from '../objects/accommodationRoomPrices';
import {
    AccommodationProductBulk,
    Product
} from '../objects/accommodationProductBulk';
import { AccommodationProductRoom } from '../objects/accommodationProductRoom';
import { DestinationReduced } from '../../destination/objects/destinationReduced';
import { Picture } from '../../picture/objects/picture';
import { Price } from '../../price/objects/price';
import { utcToZonedTime } from 'date-fns-tz';
import { DateRange } from '@material-ui/pickers';

const adultTraveler: Traveler = {
    address: '',
    address_details: '',
    birth_date: subYears(new Date(), 30),
    city: '',
    country: '',
    email: '',
    first_name: '',
    gender: 'M',
    id: 0,
    identity_doc: 'PASSPORT',
    identity_doc_country_emission: null,
    identity_doc_expiration: null,
    identity_doc_number: null,
    last_name: '',
    loyalty_cards: [],
    middle_name: '',
    nationality: '',
    phone_country_code: '',
    postal_code: '',
    room_index: null,
    title: 'MR',
    traveler_relationship: 'FAMILY',
    trip_reason: 'VACATION',
    weight: null
};

const childTraveler: Traveler = {
    address: '',
    address_details: '',
    birth_date: subYears(new Date(), 5),
    city: '',
    country: '',
    email: '',
    first_name: '',
    gender: 'M',
    id: 0,
    identity_doc: 'PASSPORT',
    identity_doc_country_emission: null,
    identity_doc_expiration: null,
    identity_doc_number: null,
    last_name: '',
    loyalty_cards: [],
    middle_name: '',
    nationality: '',
    phone_country_code: '',
    postal_code: '',
    room_index: null,
    title: 'MR',
    traveler_relationship: 'FAMILY',
    trip_reason: 'VACATION',
    weight: null
};

const initialState: AccommodationState = {
    accommodation: null,
    adults: [{...adultTraveler, room_index: 0}, {...adultTraveler, room_index: 0}],
    cart: [],
    children: [],
    composition_open: false,
    count: 0,
    current_room: null,
    destination: {
        current_version: 1,
        data: {
            bounds: "{'south': 43.68879, 'west': 7.14601, 'north': 43.78801, 'east': 7.70322}",
            country: {
                country_code: "FR",
                country_code_alpha3: "FRA",
                id: 329,
                international_name: "France",
                international_nationality: "French",
                name: "France",
                nationality: null,
                phone_code: null,
                tsv: null
            },
            cover_picture: null,
            currency: {
                id: 47,
                symbol: '€',
                iso_code: 'EUR',
                international_name: 'Euro',
                usd_factor: '1.0',
                custom_currency: [],
                name: 'Euro'
            },
            id: 1259,
            international_name: 'Monaco',
            latitude: '43.738417600000',
            latitude_center: '43.738417600000',
            localization: [{
                id: 1287,
                name: 'Monaco',
                locale: 1
            }],
            longitude: '7.424615800000',
            longitude_center: '7.424615800000',
            name: 'Monaco',
            pictures: [],
            suggested_hours: 120,
            type: 4,
            zoom_level: 13
        },
        guide_ids: [],
        id: window.location.host === 'localhost:3000' ? 1 : 1,
        parent: 144
    },
    displayed_cart: [],
    end_date: null,
    full_list: null,
    filter_active_filters: false,
    filter_air_conditioning: false,
    filter_bar: false,
    filter_cancellable: false,
    filter_change: false,
    filter_check_in: false,
    filter_conference_room: false,//TODO add to filter control
    filter_elevator: false,
    filter_kitchen: false,
    filter_meal_plan: null,
    filter_mini_bar: false,
    filter_parking: false,
    filter_private_beach: false,
    filter_receptionist: false,
    filter_recommended: false,
    filter_resort_fees: false,
    filter_restaurant: false,
    filter_room_service: false,
    filter_safe: false,
    filter_spa: false,//TODO add to filter control
    filter_star_1: false,
    filter_star_2: false,
    filter_star_3: false,
    filter_star_4: false,
    filter_star_5: false,
    filter_swimming_pool: false,
    filter_television: false,
    filter_wifi: false,
    filters_open: false,
    latitude_max: 0,
    latitude_min: 1,
    longitude_max: 0,
    longitude_min: 1,
    marginPrice: [],
    ordering: 'price_asc',
    ordering_open: false,
    page: 1,
    page_list: null,
    pre_cart: [],
    pre_cart_data: null,
    price_list: null,
    prices: null,
    refresh_prices: false,
    rooms: 1,
    rooms_prices: null,
    search: '',
    start_date: null,
    stay: 1,
    variant_open: null,
    variant_selected: null,
    show_history: null,
    hovered_accommodation: null,
    all_accommodations: [],
    add_remark_booking: null,
    show_recap: false,
    room_fees: null,
};

const filterResults = (full_list: GiataAccommodation[], state: AccommodationState) => {
    const filtered_results: GiataAccommodation[] = [];
    full_list.map((hotel) => {
        // if (state.latitude_max > parseFloat(hotel.latitude) && parseFloat(hotel.latitude) > state.longitude_min && state.longitude_max > parseFloat(hotel.longitude) && parseFloat(hotel.longitude) > state.longitude_min) {
        hotel.price = 0;
        hotel.price_currency = null;
        hotel.breakfast_price = 0;
        hotel.breakfast_currency = null;
        hotel.half_price = 0;
        hotel.half_currency = null;
        hotel.full_price = 0;
        hotel.full_currency = null;
        hotel.c_breakfast_price = 0;
        hotel.c_breakfast_currency = null;
        hotel.c_half_price = 0;
        hotel.c_half_currency = null;
        hotel.c_full_price = 0;
        hotel.c_full_currency = null;
        hotel.cancellable_price = 0;
        hotel.cancellable_currency = null;
        if (hotel.providers !== undefined) {
            hotel.providers.map((provider) => {
                if ((!state.filter_resort_fees || provider.prices.details === undefined || provider.prices.details === null || provider.prices.details.resort_fees_included || provider.prices.details.resort_fees === 0)) {
                    const price = getPriceByOwner(hotel.price, hotel.price_currency, hotel.price_resort_fees, provider.prices["prices"], provider.prices.details, 1);
                    hotel.price = price.return_price;
                    hotel.price_currency = price.return_currency;
                    hotel.price_resort_fees = price.return_resort_fees;
                    const breakfast = getPriceByOwner(hotel.breakfast_price, hotel.breakfast_currency, hotel.breakfast_resort_fees, provider.prices["breakfast_price"], provider.prices.details, 1);
                    hotel.breakfast_price = breakfast.return_price;
                    hotel.breakfast_currency = breakfast.return_currency;
                    hotel.breakfast_resort_fees = breakfast.return_resort_fees;
                    const half = getPriceByOwner(hotel.half_price, hotel.half_currency, hotel.half_resort_fees, provider.prices["half_price"], provider.prices.details, 1);
                    hotel.half_price = half.return_price;
                    hotel.half_currency = half.return_currency;
                    hotel.half_resort_fees = half.return_resort_fees;
                    const full = getPriceByOwner(hotel.full_price, hotel.full_currency, hotel.full_resort_fees, provider.prices["full_price"], provider.prices.details, 1);
                    hotel.full_price = full.return_price;
                    hotel.full_currency = full.return_currency;
                    hotel.full_resort_fees = full.return_resort_fees;
                    const c_breakfast = getPriceByOwner(hotel.c_breakfast_price, hotel.c_breakfast_currency, hotel.c_breakfast_resort_fees, provider.prices["c_breakfast_price"], provider.prices.details, 1);
                    hotel.c_breakfast_price = c_breakfast.return_price;
                    hotel.c_breakfast_currency = c_breakfast.return_currency;
                    hotel.c_breakfast_resort_fees = c_breakfast.return_resort_fees;
                    const c_half = getPriceByOwner(hotel.c_half_price, hotel.c_half_currency, hotel.c_half_resort_fees, provider.prices["c_half_price"], provider.prices.details, 1);
                    hotel.c_half_price = c_half.return_price;
                    hotel.c_half_currency = c_half.return_currency;
                    hotel.c_half_resort_fees = c_half.return_resort_fees;
                    const c_full = getPriceByOwner(hotel.c_full_price, hotel.c_full_currency, hotel.c_full_resort_fees, provider.prices["c_full_price"], provider.prices.details, 1);
                    hotel.c_full_price = c_full.return_price;
                    hotel.c_full_currency = c_full.return_currency;
                    hotel.c_full_resort_fees = c_full.return_resort_fees;
                    const cancelable = getPriceByOwner(hotel.cancellable_price, hotel.cancellable_currency, hotel.cancellable_resort_fees, provider.prices["cancellable_price"], provider.prices.details, 1);
                    hotel.cancellable_price = cancelable.return_price;
                    hotel.cancellable_currency = cancelable.return_currency;
                    hotel.cancellable_resort_fees = cancelable.return_resort_fees;
                }
            });
        }
        if ((hotel.catalog_price || hotel.price > 0) && (state.search === '' || (hotel.name !== null && hotel.name.match(new RegExp(state.search, 'i')))) && (!state.filter_recommended || (state.filter_recommended && hotel.favorite)) && (!state.filter_resort_fees || (state.filter_resort_fees && ((state.filter_meal_plan === null && !state.filter_cancellable && hotel.price_resort_fees === null) || (state.filter_meal_plan === null && state.filter_cancellable && hotel.cancellable_resort_fees === null) || (state.filter_meal_plan === 'breakfast' && !state.filter_cancellable && hotel.breakfast_resort_fees === null) || (state.filter_meal_plan === 'half_board' && !state.filter_cancellable && hotel.half_resort_fees === null) || (state.filter_meal_plan === 'full_board' && !state.filter_cancellable && hotel.full_resort_fees === null) || (state.filter_meal_plan === 'breakfast' && state.filter_cancellable && hotel.c_breakfast_resort_fees === null) || (state.filter_meal_plan === 'half_board' && state.filter_cancellable && hotel.c_half_resort_fees === null) || (state.filter_meal_plan === 'full_board' && state.filter_cancellable && hotel.c_full_resort_fees === null)))) && ((!state.filter_star_1 && !state.filter_star_2 && !state.filter_star_3 && !state.filter_star_4 && !state.filter_star_5) || ((state.filter_star_1 && hotel.stars === 1) || (state.filter_star_2 && hotel.stars === 2) || (state.filter_star_3 && hotel.stars === 3) || (state.filter_star_4 && hotel.stars === 4) || (state.filter_star_5 && hotel.stars === 5))) && (!state.filter_check_in || (state.filter_check_in && hotel.checkin)) && (!state.filter_bar || (state.filter_bar && hotel.bar)) && (!state.filter_restaurant || (state.filter_restaurant && hotel.restaurant)) && (!state.filter_kitchen || (state.filter_kitchen && hotel.kitchen)) && (!state.filter_change || (state.filter_change && hotel.change)) && (!state.filter_receptionist || (state.filter_receptionist && hotel.receptionist)) && (!state.filter_elevator || (state.filter_elevator && hotel.elevator)) && (!state.filter_parking || (state.filter_parking && hotel.parking)) && (!state.filter_swimming_pool || (state.filter_swimming_pool && hotel.pool)) && (!state.filter_room_service || (state.filter_room_service && hotel.room_service)) && (!state.filter_television || (state.filter_television && hotel.tv)) && (!state.filter_safe || (state.filter_safe && hotel.safe)) && (!state.filter_private_beach || (state.filter_private_beach && hotel.beach)) && (!state.filter_wifi || (state.filter_wifi && hotel.internet)) && (!state.filter_air_conditioning || (state.filter_room_service && hotel.clim)) && (!state.filter_mini_bar || (state.filter_mini_bar && hotel.minibar)) && ((state.filter_meal_plan === null && !state.filter_cancellable) || (state.filter_meal_plan === null && state.filter_cancellable && hotel.cancellable_price !== 0) || (state.filter_meal_plan === 'breakfast' && !state.filter_cancellable && hotel.breakfast_price !== 0) || (state.filter_meal_plan === 'half_board' && !state.filter_cancellable && hotel.half_price !== 0) || (state.filter_meal_plan === 'full_board' && !state.filter_cancellable && hotel.full_price !== 0) || (state.filter_meal_plan === 'breakfast' && state.filter_cancellable && hotel.c_breakfast_price !== 0) || (state.filter_meal_plan === 'half_board' && state.filter_cancellable && hotel.c_half_price !== 0) || (state.filter_meal_plan === 'full_board' && state.filter_cancellable && hotel.c_full_price !== 0))) {
            filtered_results.push(hotel);
        }
        // }
    });
    return filtered_results;
};

const sortResults = (list: GiataAccommodation[], state: AccommodationState) => {
    if (list === null) {
        return list;
    }
    const sorted_results = [...list];
    switch (state.ordering) {
        case 'price_asc':
            if (state.filter_meal_plan === null && !state.filter_cancellable) {
                sorted_results.sort((a, b) => {
                    return a.price - b.price;
                });
            } else if (state.filter_meal_plan === null && state.filter_cancellable) {
                sorted_results.sort((a, b) => {
                    return a.cancellable_price - b.cancellable_price;
                });
            } else if (state.filter_meal_plan === 'breakfast' && !state.filter_cancellable) {
                sorted_results.sort((a, b) => {
                    return a.breakfast_price - b.breakfast_price;
                });
            } else if (state.filter_meal_plan === 'half_board' && !state.filter_cancellable) {
                sorted_results.sort((a, b) => {
                    return a.half_price - b.half_price;
                });
            } else if (state.filter_meal_plan === 'full_board' && !state.filter_cancellable) {
                sorted_results.sort((a, b) => {
                    return a.full_price - b.full_price;
                });
            } else if (state.filter_meal_plan === 'breakfast' && state.filter_cancellable) {
                sorted_results.sort((a, b) => {
                    return a.c_breakfast_price - b.c_breakfast_price;
                });
            } else if (state.filter_meal_plan === 'half_board' && state.filter_cancellable) {
                sorted_results.sort((a, b) => {
                    return a.c_half_price - b.c_half_price;
                });
            } else if (state.filter_meal_plan === 'full_board' && state.filter_cancellable) {
                sorted_results.sort((a, b) => {
                    return a.c_full_price - b.c_full_price;
                });
            }
            break;
        case 'price_dec':
            if (state.filter_meal_plan === null && !state.filter_cancellable) {
                sorted_results.sort((a, b) => {
                    return b.price - a.price;
                });
            } else if (state.filter_meal_plan === null && state.filter_cancellable) {
                sorted_results.sort((a, b) => {
                    return b.cancellable_price - a.cancellable_price;
                });
            } else if (state.filter_meal_plan === 'breakfast' && !state.filter_cancellable) {
                sorted_results.sort((a, b) => {
                    return b.breakfast_price - a.breakfast_price;
                });
            } else if (state.filter_meal_plan === 'half_board' && !state.filter_cancellable) {
                sorted_results.sort((a, b) => {
                    return b.half_price - a.half_price;
                });
            } else if (state.filter_meal_plan === 'full_board' && !state.filter_cancellable) {
                sorted_results.sort((a, b) => {
                    return b.full_price - a.full_price;
                });
            } else if (state.filter_meal_plan === 'breakfast' && state.filter_cancellable) {
                sorted_results.sort((a, b) => {
                    return b.c_breakfast_price - a.c_breakfast_price;
                });
            } else if (state.filter_meal_plan === 'half_board' && state.filter_cancellable) {
                sorted_results.sort((a, b) => {
                    return b.c_half_price - a.c_half_price;
                });
            } else if (state.filter_meal_plan === 'full_board' && state.filter_cancellable) {
                sorted_results.sort((a, b) => {
                    return b.c_full_price - a.c_full_price;
                });
            }
            break;
        case 'name_asc':
            sorted_results.sort((a, b) => {
                return a.name.localeCompare(b.name);
            });
            break;
        case 'name_dec':
            sorted_results.sort((a, b) => {
                return b.name.localeCompare(a.name);
            });
            break;
        case 'stars_asc':
            sorted_results.sort((a, b) => {
                return a.stars - b.stars;
            });
            break;
        case 'stars_dec':
            sorted_results.sort((a, b) => {
                return b.stars - a.stars;
            });
            break;
    }
    return sorted_results;
};

const getPageResults = (list: GiataAccommodation[], page: number) => {
    const page_list = [];
    for (let i = 30 * (page - 1); i < 30 * page && i < list.length; i++) {
        page_list.push(list[i]);
    }
    return page_list;
};

const getPriceByOwner = (current_price, current_currency, current_resort_fees, prices, details, owner) => {
    if (prices === null) {
        const return_price = current_price;
        const return_currency = current_currency;
        const return_resort_fees = current_resort_fees;
        return { return_price, return_currency, return_resort_fees };
    } else {
        let return_price = current_price;
        let return_currency = current_currency;
        let return_resort_fees = current_resort_fees;
        const price = prices?.[0];
        if ((current_price === 0 || price.selling_price < current_price)) {
            return_price = parseFloat(price.selling_price);
            return_currency = price.selling_currency;
            return_resort_fees = details !== undefined && details !== null && !details.resort_fees_included && details.resort_fees !== 0 ? {
                fees: details.resort_fees,
                currency: details.resort_fees_currency_id
            } : null;
        }
        // prices.map((price) => {
        //     if (price.owner === owner && (current_price === 0 || price.selling_price < current_price)) {
        //         return_price = parseFloat(price.selling_price);
        //         return_currency = price.selling_currency;
        //         return_resort_fees = details !== undefined && details !== null && !details.resort_fees_included && details.resort_fees !== 0 ? {
        //             fees: details.resort_fees,
        //             currency: details.resort_fees_currency_id
        //         } : null;
        //     }
        // });
        return { return_price, return_currency, return_resort_fees };
    }
};

export const accommodationSlice = createSlice({
    name: 'accommodation',
    initialState,
    reducers: {
        addCartRooms: (state, action: PayloadAction<any>) => {
            const cart = [...state.cart, ...action.payload];
            const displayed_cart: (AccommodationProduct & {rooms: AccommodationProduct[]})[] = [];
            cart.map(item => {
                let same = false;
                displayed_cart.map(sort_item => {
                    if (item.is_displayed === sort_item.is_displayed && sort_item.hotel?.[0].id === item.hotel?.[0].id && (sort_item.provider === item.provider) && sort_item.start_date === item.start_date &&
                        sort_item.end_date === item.end_date && sort_item.on_request === item.on_request && sort_item.is_optional === item.is_optional && sort_item.variant === item.variant &&
                        ((sort_item.booking_status === null && item.booking_status === null) || (sort_item.booking_status !== null && item.booking_status !== null &&
                            ((sort_item.booking_status.status_booking !== 'CANCELLED' && item.booking_status.status_booking !== 'CANCELLED' && sort_item.booking_process_state !== 'CANCEL' && item.booking_process_state !== 'CANCEL') ||
                                (sort_item.booking_status.status_booking === 'CANCELLED' && item.booking_status.status_booking === 'CANCELLED'))))) {
                        same = true;
                        sort_item.rooms.push(item);
                        sort_item.rooms.sort((a, b) => a.id - b.id);
                    }
                });
                if (!same) {
                    displayed_cart.push({...item, rooms: [item]});
                }
            });
            state.cart = cart;
            state.displayed_cart = displayed_cart;
        },
        setCartRooms: (state, action: PayloadAction<AccommodationProductBulk[]>) => {
            const cart = [];
            action.payload.map(accommodation => {
                const hotel = {
                    blacklist: accommodation.blacklist,
                    city_name: accommodation.city_name,
                    country_code: accommodation.country_code,
                    cover_picture: accommodation.cover_picture,
                    custom_pictures: accommodation.custom_pictures,
                    favorite: accommodation.favorite,
                    hotel_sup: accommodation.hotel_sup,
                    house_number: accommodation.house_number,
                    id: accommodation.id,
                    latitude: accommodation.latitude,
                    longitude: accommodation.longitude,
                    name: accommodation.name,
                    pictures: accommodation.pictures,
                    remark_favorite: accommodation.remark_favorite,
                    stars: accommodation.stars,
                    street: accommodation.street,
                    zip_code: accommodation.zip_code
                };
                if (accommodation.products) {
                    accommodation.products.map(room => {
                        hotel.id = room.hotel;
                        cart.push({...room, hotel: [hotel]});
                    });
                }
            });
            const displayed_cart: (AccommodationProduct & {rooms: AccommodationProduct[]})[] = [];
            cart.map(item => {
                let same = false;
                displayed_cart.map(sort_item => {
                    if (item.is_displayed === sort_item.is_displayed && sort_item.hotel?.[0].id === item.hotel?.[0].id && (sort_item.provider === item.provider) && sort_item.start_date === item.start_date &&
                        sort_item.end_date === item.end_date && sort_item.on_request === item.on_request && sort_item.is_optional === item.is_optional && sort_item.variant === item.variant &&
                        ((sort_item.booking_status === null && item.booking_status === null) || (sort_item.booking_status !== null && item.booking_status !== null &&
                            ((sort_item.booking_status.status_booking !== 'CANCELLED' && item.booking_status.status_booking !== 'CANCELLED' && sort_item.booking_process_state !== 'CANCEL' && item.booking_process_state !== 'CANCEL') ||
                                (sort_item.booking_status.status_booking === 'CANCELLED' && item.booking_status.status_booking === 'CANCELLED'))))) {
                        same = true;
                        sort_item.rooms.push(item);
                        sort_item.rooms.sort((a, b) => a.id - b.id);
                    }
                });
                if (!same) {
                    displayed_cart.push({...item, rooms: [item]});
                }
            });
            state.cart = cart;
            state.displayed_cart = displayed_cart;
        },
        addPreCart: (state, action: PayloadAction<AccommodationRoom>) => {
            const room = action.payload;
            let variant_selected = null;
            room.variants.map(variant => {
                if (variant.identifier === state.variant_selected) {
                    variant_selected = variant;
                }
            });
            let current_room = state.current_room;
            if (state.rooms_prices !== null) {
                for (let i = 0; i < state.rooms_prices.length; i++) {
                    if (state.rooms_prices[i].passenger_group_id === state.current_room) {
                        if (i + 1 < state.rooms_prices.length) {
                            current_room = state.rooms_prices[i + 1].passenger_group_id;
                        } else {
                            current_room = null;
                        }
                    }
                    for (let j = 0; j < state.rooms_prices[i].rooms.length; j++) {
                        if (room.room_code === state.rooms_prices[i].rooms[j].room_code) {
                            state.rooms_prices[i].rooms[j].remaining_stock = state.rooms_prices[i].rooms[j].remaining_stock !== null ? state.rooms_prices[i].rooms[j].remaining_stock - 1 : state.rooms_prices[i].rooms[j].remaining_stock;
                            for (let k = 0; k < state.rooms_prices[i].rooms[j].variants.length; k++) {
                                state.rooms_prices[i].rooms[j].variants[k].remaining_stock = state.rooms_prices[i].rooms[j].variants[k].remaining_stock !== null ? state.rooms_prices[i].rooms[j].variants[k].remaining_stock - 1 : state.rooms_prices[i].rooms[j].variants[k].remaining_stock;
                            }
                        }
                    }
                }
            }
            const pre_cart = [...state.pre_cart];
            if (state.pre_cart_data !== null && state.variant_selected !== null && variant_selected !== null) {
                pre_cart.push({...state.pre_cart_data, is_optional: false, passenger_group_id: state.current_room, room: room, room_identifier: state.variant_selected, start_destination: null, variant: variant_selected});
            }
            state.pre_cart = pre_cart;
            state.variant_open = null;
            state.variant_selected = null;
            state.current_room = current_room;
        },
        removeCartRoom: (
            state,
            action: PayloadAction<{id: number}>
        ): void => {
            for (const accommodation of state.displayed_cart) {
                accommodation.rooms = accommodation.rooms.filter((item) => {
                    return item.id !== action.payload.id;
                });
            }
        },
        deleteRoom: (state, action) => {
            const cart = [...state.cart];
            const displayed_cart = [...state.displayed_cart];
            state.cart = cart.filter((room) => room.id !== action.payload);
            state.displayed_cart = displayed_cart.map(accommodation => {
                return {
                    ...accommodation,
                    rooms: accommodation.rooms.filter(room => room.id !== action.payload),
                };
            });
        },
        setAccommodation: (state, action: PayloadAction<null | {id: number, data: Accommodation[]}>) => {
            if (action.payload === null) {
                state.accommodation = null;
                state.variant_open = null;
                state.variant_selected = null;
                state.rooms_prices = null;
                state.pre_cart = [];
                state.pre_cart_data = null;
                state.show_recap = false;
            } else {
                let accommodation = action.payload.data?.[0];
                action.payload.data.map(data => {
                    if (!data.is_giata) {
                        accommodation = data;
                    }
                });
                let prev_section = null;
                for (let i = 0; i < accommodation.content.length; i++) {
                    if (accommodation.content[i].section === prev_section) {
                        accommodation.content[i - 1].content += `\n${accommodation.content[i].content}`;
                        accommodation.content.splice(i, 1);
                        i--;
                    }
                    prev_section = accommodation.content[i].section;
                }
                if (accommodation.content.length > 0 && accommodation.content?.[0].section > 200) {
                    accommodation.content.reverse();
                }
                if (accommodation.pictures.length < 5) {
                    for (let i = accommodation.pictures.length; i < 5; i++) {
                        accommodation.pictures.push({id: 0, url: '/img/hotel.png'});
                    }
                }
                state.accommodation = accommodation;
            }
        },
        resetAccommodationPrices: (state) => {
            state.prices = null;
        },
        setAccommodationPrices: (state, action: PayloadAction<{prices: any, provider_id: number}>) => {
            const prices = state.prices !== null ? state.prices : [];
            if (action.payload.prices.length > 0) {
                prices.push({...action.payload.prices?.[0], provider_id: action.payload.provider_id});
            }
            prices.sort((a, b) => {
                return parseFloat(a.prices?.[0].selling_price) - parseFloat(b.prices?.[0].selling_price);
            });
            state.prices = prices;
        },
        setAccommodationRoomsPrices: (state, action: PayloadAction<{rooms_prices: AccommodationRoomPrices[], pre_cart_data: PreCartData}>) => {
            const rooms_prices = action.payload.rooms_prices;
            rooms_prices.map(group => {
                group.rooms.map(room => {
                    room.variants.sort((a, b) => {
                        return parseFloat(a.price_stay?.[0].selling_price) - parseFloat(b.price_stay?.[0].selling_price);
                    });
                    room.minimal_price = parseFloat(room.variants?.[0].price_stay?.[0].selling_price);
                    room.minimal_price_currency = room.variants?.[0].price_stay?.[0].selling_currency;
                });
            });
            state.current_room = rooms_prices.length > 0 ? rooms_prices?.[0].passenger_group_id : null;
            state.rooms_prices = rooms_prices;
            state.pre_cart = [];
            state.pre_cart_data = action.payload.pre_cart_data;
        },
        setAccommodationsMultiPrices: (state, action) => {
            if (state.full_list !== null) {
                state.full_list.map(accommodation => {
                    action.payload.map(price => {
                        if (accommodation.id === price.hotel_id && price.prices !== undefined && price.prices !== null && price.prices.length > 0) {
                            if (accommodation.catalog_price === undefined || !accommodation.catalog_price) {
                                accommodation.catalog_price = price.details !== null ? price.details.catalog_price : false;
                            }
                            if (accommodation.providers !== undefined) {
                                let already_added = false;
                                accommodation.providers.map(provider => {
                                    if (provider.id === price.details.provider_id) {
                                        provider.prices = price;
                                        already_added = true;
                                    }
                                });
                                if (!already_added) {
                                    accommodation.providers.push({
                                        id: price.details.provider_id,
                                        prices: price
                                    });
                                }
                            }
                        }
                    });
                });
                state.price_list = sortResults(filterResults([...state.full_list], state), state);
                state.page_list = getPageResults([...state.price_list], state.page);
                state.count = state.price_list.length;
            }
        },
        setAccommodationsPrices: (state, action: PayloadAction<{prices: any, provider_id: number}>) => {
            if (state.full_list !== null) {
                state.full_list.map(accommodation => {
                    action.payload.prices.map(price => {
                        if (accommodation.id === price.hotel_id && price.prices !== undefined && price.prices !== null && price.prices.length > 0) {
                            if (accommodation.catalog_price === undefined || !accommodation.catalog_price) {
                                accommodation.catalog_price = price.details !== null ? price.details.catalog_price : false;
                            }
                            if (accommodation.providers !== undefined) {
                                let already_added = false;
                                accommodation.providers.map(provider => {
                                    if (provider.id === action.payload.provider_id) {
                                        provider.prices = price;
                                        already_added = true;
                                    }
                                });
                                if (!already_added) {
                                    accommodation.providers.push({
                                        id: action.payload.provider_id,
                                        prices: price
                                    });
                                }
                            }
                        }
                    });
                });
                state.price_list = sortResults(filterResults([...state.full_list], state), state);
                state.page_list = getPageResults([...state.price_list], state.page);
                state.count = state.price_list.length;
            }
        },
        setBounds: (state, action: PayloadAction<{latitude_max: number, longitude_max: number, latitude_min: number, longitude_min: number}>) => {
            const { latitude_max, latitude_min, longitude_max, longitude_min } = action.payload;
            state.latitude_max = latitude_max;
            state.latitude_min = latitude_min;
            state.longitude_max = longitude_max;
            state.longitude_min = longitude_min;
        },
        setFilters: (state, action: PayloadAction<{filter_air_conditioning: boolean, filter_bar: boolean, filter_cancellable: boolean,
            filter_change: boolean, filter_check_in: boolean, filter_conference_room: boolean,
            filter_elevator : boolean, filter_kitchen: boolean, filter_meal_plan: null | 'breakfast' | 'half_board' | 'full_board',
            filter_mini_bar: boolean, filter_parking: boolean, filter_private_beach: boolean,
            filter_receptionist: boolean, filter_resort_fees: boolean,
            filter_restaurant: boolean, filter_room_service: boolean, filter_safe: boolean,
            filter_spa: boolean, filter_star_1: boolean, filter_star_2: boolean, filter_star_3: boolean,
            filter_star_4: boolean, filter_star_5: boolean, filter_swimming_pool: boolean,
            filter_television: boolean, filter_wifi: boolean}>) => {
            const { filter_air_conditioning, filter_bar, filter_cancellable, filter_change, filter_check_in, filter_conference_room, filter_elevator , filter_kitchen, filter_meal_plan, filter_mini_bar, filter_parking, filter_private_beach, filter_receptionist, filter_resort_fees, filter_restaurant, filter_room_service, filter_safe, filter_spa, filter_star_1, filter_star_2, filter_star_3, filter_star_4, filter_star_5, filter_swimming_pool, filter_television, filter_wifi } = action.payload;
            state.filters_open = false;
            state.filter_air_conditioning = filter_air_conditioning;
            state.filter_bar = filter_bar;
            state.filter_cancellable = filter_cancellable;
            state.filter_change = filter_change;
            state.filter_check_in = filter_check_in;
            state.filter_conference_room = filter_conference_room;
            state.filter_elevator = filter_elevator;
            state.filter_kitchen = filter_kitchen;
            state.filter_meal_plan = filter_meal_plan;
            state.filter_mini_bar = filter_mini_bar;
            state.filter_parking = filter_parking;
            state.filter_private_beach = filter_private_beach;
            state.filter_receptionist = filter_receptionist;
            state.filter_resort_fees = filter_resort_fees;
            state.filter_restaurant = filter_restaurant;
            state.filter_room_service = filter_room_service;
            state.filter_safe = filter_safe;
            state.filter_spa = filter_spa;
            state.filter_star_1 = filter_star_1;
            state.filter_star_2 = filter_star_2;
            state.filter_star_3 = filter_star_3;
            state.filter_star_4 = filter_star_4;
            state.filter_star_5 = filter_star_5;
            state.filter_swimming_pool = filter_swimming_pool;
            state.filter_television = filter_television;
            state.filter_wifi = filter_wifi;
            if (state.full_list !== null) {
                state.price_list = sortResults(filterResults([...state.full_list], state), state);
                state.page_list = getPageResults([...state.price_list], state.page);
            }
        },
        setGiataAccommodations: (state, action: PayloadAction<GiataAccommodation[]>) => {
            if (state.full_list === null) {
                action.payload.map(accommodation => {
                    accommodation.providers = [];
                    accommodation.price = 0;
                    accommodation.price_currency = null;
                    accommodation.breakfast_price = 0;
                    accommodation.breakfast_currency = null;
                    accommodation.half_price = 0;
                    accommodation.half_currency = null;
                    accommodation.full_price = 0;
                    accommodation.full_currency = null;
                    accommodation.cancellable_price = 0;
                    accommodation.cancellable_currency = null;
                    accommodation.c_breakfast_price = 0;
                    accommodation.c_breakfast_currency = null;
                    accommodation.c_half_price = 0;
                    accommodation.c_half_currency = null;
                    accommodation.c_full_price = 0;
                    accommodation.c_full_currency = null;
                    accommodation.start_date = state.start_date;
                    accommodation.end_date = state.end_date;
                    accommodation.stay = state.start_date !== null && state.end_date !== null ? differenceInCalendarDays(new Date(state.end_date), new Date(state.start_date)) : 1;
                });
                state.refresh_prices = !state.refresh_prices;
                state.full_list = action.payload;
            } else {
                let full_list = [...state.full_list];
                const new_accommodations: GiataAccommodation[] = [];
                action.payload.map(accommodation => {
                    let exist = false;
                    for (let i = 0; i < full_list?.length; i++) {
                        if (full_list[i].id === accommodation.id) {
                            if (!(full_list[i].start_date === state.start_date) || !(full_list[i].end_date === state.end_date)) {
                                full_list[i].providers = [];
                                full_list[i].price = 0;
                                full_list[i].price_currency = null;
                                full_list[i].breakfast_price = 0;
                                full_list[i].breakfast_currency = null;
                                full_list[i].half_price = 0;
                                full_list[i].half_currency = null;
                                full_list[i].full_price = 0;
                                full_list[i].full_currency = null;
                                full_list[i].cancellable_price = 0;
                                full_list[i].cancellable_currency = null;
                                full_list[i].c_breakfast_price = 0;
                                full_list[i].c_breakfast_currency = null;
                                full_list[i].c_half_price = 0;
                                full_list[i].c_half_currency = null;
                                full_list[i].c_full_price = 0;
                                full_list[i].c_full_currency = null;
                                full_list[i].start_date = state.start_date;
                                full_list[i].end_date = state.end_date;
                                full_list[i].stay = state.start_date !== null && state.end_date !== null ? differenceInCalendarDays(new Date(state.end_date), new Date(state.start_date)) : 1;
                            }
                            exist = true;
                            break;
                        }
                    }
                    if (!exist) {
                        new_accommodations.push(accommodation);
                    }
                });
                new_accommodations.map(accommodation => {
                    accommodation.providers = [];
                    accommodation.price = 0;
                    accommodation.price_currency = null;
                    accommodation.breakfast_price = 0;
                    accommodation.breakfast_currency = null;
                    accommodation.half_price = 0;
                    accommodation.half_currency = null;
                    accommodation.full_price = 0;
                    accommodation.full_currency = null;
                    accommodation.cancellable_price = 0;
                    accommodation.cancellable_currency = null;
                    accommodation.c_breakfast_price = 0;
                    accommodation.c_breakfast_currency = null;
                    accommodation.c_half_price = 0;
                    accommodation.c_half_currency = null;
                    accommodation.c_full_price = 0;
                    accommodation.c_full_currency = null;
                    accommodation.start_date = state.start_date;
                    accommodation.end_date = state.end_date;
                    accommodation.stay = state.start_date !== null && state.end_date !== null ? differenceInCalendarDays(new Date(state.end_date), new Date(state.start_date)) : 1;
                });
                full_list = full_list.concat(new_accommodations);
                const price_list = sortResults(filterResults([...full_list], state), state);
                const page_list = getPageResults([...price_list], state.page);
                state.full_list = full_list;
                state.price_list = price_list;
                state.page_list = page_list;
                state.count = price_list.length;
                state.refresh_prices = !state.refresh_prices;
            }
        },
        setMarginPrice: (state, action: PayloadAction<Price[]>) => {
            state.marginPrice = action.payload;
        },
        toggleAdult: (state, action: PayloadAction<{room_index: number, adult_index: number}>) => {
            const { room_index, adult_index } = action.payload;
            const adults = [...state.adults];
            const adult = {...adults[adult_index]};
            adult.room_index = adult.room_index !== room_index ? room_index : null;
            adults[adult_index] = adult;
            state.adults = adults;
        },
        toggleChild: (state, action: PayloadAction<{room_index: number, child_index: number}>) => {
            const { room_index, child_index } = action.payload;
            const children = [...state.children];
            const child = {...children[child_index]};
            child.room_index = child.room_index !== room_index ? room_index : null;
            children[child_index] = child;
            state.children =children;
        },
        toggleCompositionDialog: (state) => {
            state.composition_open = !state.composition_open;
        },
        toggleOrderingDialog: (state) => {
            state.ordering_open = !state.ordering_open;
        },
        toggleFiltersDialog: (state) => {
            state.filters_open = !state.filters_open;
        },
        toggleRecommended: (state) => {
            state.filter_recommended = !state.filter_recommended;
            if (state.full_list !== null) {
                state.price_list = sortResults(filterResults([...state.full_list], state), state);
                state.page_list = getPageResults([...state.price_list], state.page);
            }
        },
        updateAccommodatations: (
            state,
            action: PayloadAction<AccommodationProductRoom[]>
        ): void => {
            state.displayed_cart = state.displayed_cart.map((item) => {
                return {
                    ...item,
                    is_optional: action.payload.find((accommodation) => accommodation.id === item.id)?.is_optional,
                    custom_information: action.payload?.[0].custom_information,
                    rooms: item.rooms.map((room) => {
                        let updatedRoom = action.payload.find((item) => {
                            return item.id === room.id;
                        });
                        if (updatedRoom !== undefined) {
                            updatedRoom.group_passenger = updatedRoom.group_passenger.id;
                        }
                        return updatedRoom ?? room;
                    })
                };
            });
        },
        updateCartRoom: (
            state,
            action: PayloadAction<AccommodationProductRoom[]>
        ): void => {
            // for (const accommodation of state.displayed_cart) {
            //     accommodation.rooms = accommodation.rooms.map((room) => {
            //         if (room.id === action.payload.room.id) {
            //             return action.payload.room;
            //         }
            //         return room;
            //     });
            // }
            let new_cart = state.displayed_cart.map((item) => {
                return {
                    ...item,
                    rooms: item.rooms.map((room) => {
                        let updatedRoom = room;
                        action.payload.map(response => {
                            if (response.id === undefined) {
                                response.map(item => {
                                    if (room.id === item.id) {
                                        updatedRoom = item;
                                    }
                                });
                            } else {
                                if (room.id === response.id) {
                                    updatedRoom = response;
                                }
                            }
                        });
                        // let updatedRoom = action.payload.find((item) => {
                        //     if (item.id !== undefined) {
                        //         console.log('item.id : ', item.id);
                        //         console.log('room.id : ', room.id);
                        //         return item.id === room.id;
                        //     } else {
                        //         return item.find((el) => {
                        //             console.log('el.id : ', el.id);
                        //             console.log('room.id : ', room.id);
                        //             return el.id === room.id;
                        //         });
                        //     }
                        // });
                        return updatedRoom !== undefined ? updatedRoom.id !== undefined ? updatedRoom : updatedRoom?.[0] : room;
                    })
                };
            });
            console.log('new_cart:', new_cart);
            state.displayed_cart = new_cart;
        },
        updateCartRoomWithModification: (
            state,
            action: PayloadAction<AccommodationProductRoom>
        ): void => {
            action.payload.group_passenger = action.payload.group_passenger.id;
            let added = false;
            let copy = null;
            for (let i = 0; i < state.displayed_cart.length; i++) {
                if (state.displayed_cart[i].hotel?.[0].id === action.payload.hotel?.[0].id) {
                    copy = {...state.displayed_cart[i]};
                    if (isSameDay(new Date(state.displayed_cart[i].start_date), new Date(action.payload.start_date)) && isSameDay(new Date(state.displayed_cart[i].end_date), new Date(action.payload.end_date))) {
                        let found = false;
                        for (let j = 0; j < state.displayed_cart[i].rooms.length; j++) {
                            if (state.displayed_cart[i].rooms[j].id === action.payload.id) {
                                found = true;
                                added = true;
                                state.displayed_cart[i].rooms[j] = action.payload;
                                break;
                            }
                        }
                        if (!found) {
                            added = true;
                            state.displayed_cart[i].rooms.push(action.payload);
                        }
                    } else {
                        for (let j = 0; j < state.displayed_cart[i].rooms.length; j++) {
                            if (state.displayed_cart[i].rooms[j].id === action.payload.id) {
                                state.displayed_cart[i].rooms.splice(j, 1);
                                if (state.displayed_cart[i].rooms.length === 0) {
                                    state.displayed_cart.splice(i, 1);
                                    i -= 1;
                                }
                                copy = {...action.payload};
                                break;
                            }
                        }
                    }
                }
            }
            if (!added && copy !== null) {
                copy.rooms = [];
                copy.rooms.push(action.payload);
                state.displayed_cart.push(copy);
            }
            // for (const accommodation of state.displayed_cart) {
            //     let index_to_delete = null;
            //     accommodation.rooms = accommodation.rooms.map((room, room_index) => {
            //         if (room.id === action.payload.id) {
            //             if (isSameDay(new Date(room.start_date), new Date(action.payload.start_date)) &&
            //               isSameDay(new Date(room.end_date), new Date(action.payload.end_date))) {
            //                 return action.payload;
            //             } else {
            //                 index_to_delete = room_index;
            //             }
            //         }
            //         return room;
            //     });
            //     if (index_to_delete !== null) {
            //         accommodation.rooms.slice(index_to_delete, 1);
            //         const accommodation_cpy = {...accommodation};
            //         accommodation_cpy.rooms = [];
            //         accommodation_cpy.rooms.push(action.payload);
            //         state.displayed_cart.
            //     }
            // }
        },
        updateDates: (state, action: PayloadAction<null | DateRange<Date>>) => {
            let stay = state.stay;
            if (action.payload !== null && action.payload[0] !== null && action.payload[1] !== null) {
                stay = differenceInCalendarDays(action.payload[1], action.payload[0]);
            }
            state.start_date = action.payload[0];
            state.end_date = action.payload[1];
            state.stay = stay;
        },
        updateDestination: (state, action: PayloadAction<null | DestinationReduced>) => {
            state.destination = action.payload;
        },
        updateEndDate: (state, action: PayloadAction<null | Date>) => {
            let stay = state.stay;
            if (state.start_date !== null && action.payload !== null) {
                stay = differenceInCalendarDays(action.payload, state.start_date);
            }
            state.end_date = action.payload;
            state.stay = stay;
        },
        updateOrdering: (state, action: PayloadAction<'price_asc' | 'price_dec' | 'name_asc' | 'name_dec' | 'stars_asc' | 'stars_dec'>) => {
            state.ordering = action.payload;
            if (state.full_list !== null) {
                state.price_list = sortResults(filterResults([...state.full_list], state), state);
                state.page_list = getPageResults([...state.price_list], state.page);
                state.count = state.price_list.length;
            }
            state.ordering_open = false;
        },
        updateRooms: (state, action: PayloadAction<number>) => {
            state.rooms = action.payload;
        },
        updateRoomsAfterBooking: (
            state,
            action: PayloadAction<AccommodationProductRoom[]>
        ): void => {
            let new_cart = state.displayed_cart.map((item) => {
                return {
                    ...item,
                    rooms: item.rooms.map((room) => {
                        let updatedRoom = room;
                        action.payload.map(response => {
                            response.map(item => {
                                if (room.id === item.id) {
                                    if (item.group_passenger && item.group_passenger.id) {
                                        item.group_passenger = item.group_passenger.id;
                                    }
                                    updatedRoom = item;
                                }
                            });
                        });
                        // let updatedRoom = action.payload.find((item) => {
                        //     if (item.id !== undefined) {
                        //         console.log('item.id : ', item.id);
                        //         console.log('room.id : ', room.id);
                        //         return item.id === room.id;
                        //     } else {
                        //         return item.find((el) => {
                        //             console.log('el.id : ', el.id);
                        //             console.log('room.id : ', room.id);
                        //             return el.id === room.id;
                        //         });
                        //     }
                        // });
                        console.log('updatedRoom : ', updatedRoom);
                        return updatedRoom !== undefined ? updatedRoom.id !== undefined ? updatedRoom : updatedRoom?.[0] : room;
                    })
                };
            });
            console.log('new_cart:', new_cart);
            state.displayed_cart = new_cart;
        },
        updateSearch: (state, action: PayloadAction<string>) => {
            state.search = action.payload;
            if (state.full_list !== null) {
                state.price_list = sortResults(filterResults([...state.full_list], state), state);
                state.page_list = getPageResults([...state.price_list], state.page);
                state.count = state.price_list.length;
            }
        },
        updateStartDate: (state, action: PayloadAction<null | Date>) => {
            let stay = state.stay;
            if (state.end_date !== null && action.payload !== null) {
                stay = differenceInCalendarDays(state.end_date, action.payload);
            }
            state.start_date = action.payload;
            state.stay = stay;
        },
        updateVariantOpen: (state, action: PayloadAction<null | string>) => {
            state.variant_open = action.payload;
        },
        updateVariantSelected: (state, action: PayloadAction<null | string>) => {
            state.variant_selected = action.payload;
        },
        setShowHistory: (state, action: PayloadAction<null | {room_id: number, room_index: number}>) => {
            state.show_history = action.payload;
        },
        setHoveredAccommodation: (state, action: PayloadAction<GiataAccommodation>) => {
            state.hovered_accommodation = action.payload;
        },
        setAllAccommodation: (state, action: PayloadAction<Accommodation[]>) => {
            state.all_accommodations = action.payload.map((accommodation) => {
                return {
                    ...accommodation,
                    products: accommodation.products.sort((a, b) => a.id - b.id)
                };
            });
            console.log("state.all_accommodations : ", state.all_accommodations)
        },
        updateAllAccommodationRoomRequestAdditionalNightDate: (state, action: PayloadAction<AccommodationProductRoom>) => {
            state.all_accommodations = state.all_accommodations.map((accommodation) => {
                return {
                    ...accommodation,
                    products: accommodation.products.map((product) => {
                        if (product.id === action.payload.id) {
                            return {
                                ...product,
                                requested_additional_night_date: action.payload.requested_additional_night_date
                            };
                        }
                        return product;
                    })
                }
            })
        },
        updateAllAccommodationRoomDateChange: (state, action: PayloadAction<AccommodationProductRoom>) => {
            state.all_accommodations = state.all_accommodations.map((accommodation) => {
                return {
                    ...accommodation,
                    products: accommodation.products.map((product) => {
                        if (product.id === action.payload.id) {
                            return {
                                ...product,
                                start_date: action.payload.start_date,
                                end_date: action.payload.end_date
                            };
                        }
                        return product;
                    })
                }
            })
        },
        updateAllAccommodationRoomRemarkBooking: (state, action: PayloadAction<AccommodationProductRoom>) => {
            state.all_accommodations = state.all_accommodations.map((accommodation) => {
                return {
                    ...accommodation,
                    products: accommodation.products.map((product) => {
                        if (product.id === action.payload.id) {
                            return {
                                ...product,
                                remark_booking: action.payload.remark_booking
                            };
                        }
                        return product;
                    })
                }
            })
        },
        updateAllAccommodationRoomFees: (state, action: PayloadAction<AccommodationProductRoom>) => {
            state.all_accommodations = state.all_accommodations.map((accommodation) => {
                return {
                    ...accommodation,
                    products: accommodation.products.map((product) => {
                        if (product.id === action.payload.id) {
                            console.log("found product for fees and updated");
                            return {
                                ...product,
                                fees: action.payload.fees
                            };
                        }
                        return product;
                    })
                };
            });
            console.log("state.all_accommodations : ", state.all_accommodations);
            state.room_fees = null;
        },
        setRoomFees: (state, action) => {
            state.room_fees = action.payload;
        },
        updateAllAccommodationRoomComment: (state, action: PayloadAction<AccommodationProductRoom>) => {
            state.all_accommodations = state.all_accommodations.map((accommodation) => {
                return {
                    ...accommodation,
                    products: accommodation.products.map((product) => {
                        if (product.id === action.payload.id) {
                            return {
                                ...product,
                                custom_information: action.payload.custom_information
                            };
                        }
                        return product;
                    })
                }
            })
        },
        showRecap: (state, action) => {
            state.show_recap = action.payload;
        },
    },
    extraReducers: builder => {
        builder.addCase(setEvent, (state, action) => {
            const start_date = action.payload.event_start_date !== null ? utcToZonedTime(new Date(action.payload.event_start_date), 'Etc/UTC') : null;
            const end_date = action.payload.event_end_date !== null ? utcToZonedTime(new Date(action.payload.event_end_date), 'Etc/UTC') : null;
            let stay = 1;
            if (start_date !== null && end_date !== null) {
                stay = differenceInCalendarDays(end_date, start_date);
            }
            state.start_date = start_date;
            state.end_date = end_date;
            state.stay = stay;
        });
    }
});

export const {
    addCartRooms,
    setCartRooms,
    addPreCart,
    removeCartRoom,
    deleteRoom,
    setAccommodation,
    resetAccommodationPrices,
    setAccommodationPrices,
    setAccommodationRoomsPrices,
    setAccommodationsMultiPrices,
    setAccommodationsPrices,
    setBounds,
    setFilters,
    setGiataAccommodations,
    setMarginPrice,
    toggleAdult,
    toggleChild,
    toggleCompositionDialog,
    toggleOrderingDialog,
    toggleFiltersDialog,
    toggleRecommended,
    updateAccommodatations,
    updateCartRoom,
    updateCartRoomWithModification,
    updateDates,
    updateDestination,
    updateEndDate,
    updateOrdering,
    updateRooms,
    updateRoomsAfterBooking,
    updateSearch,
    updateStartDate,
    updateVariantOpen,
    updateVariantSelected,
    setShowHistory,
    setHoveredAccommodation,
    setAllAccommodation,
    updateAllAccommodationRoomRequestAdditionalNightDate,
    updateAllAccommodationRoomDateChange,
    updateAllAccommodationRoomRemarkBooking,
    updateAllAccommodationRoomComment,
    updateAllAccommodationRoomFees,
    setRoomFees,
    showRecap,
} = accommodationSlice.actions;

export default accommodationSlice.reducer;